import React, { useEffect, useState } from "react";
import { Copy, Plus, Trash2, ExternalLink, Code2 } from "lucide-react";
import { toast } from "sonner";
import { embedApi } from "../lib/ledgerApi";

const FE_ORIGIN = (typeof window !== "undefined") ? window.location.origin : "";

function fmtTime(iso) {
  if (!iso) return "—";
  return iso.slice(0, 19).replace("T", " ");
}

export default function AgentEmbedTab({ agentId, agent }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [last, setLast] = useState(null); // last minted (with bare token)
  const [form, setForm] = useState({
    merchant_vpa: "",
    currency: "NEO",
    max_amount: "",
    description: "",
    category: "",
    expires_in_minutes: 60,
  });

  const reload = async () => {
    try {
      const r = await embedApi.list(agentId);
      setTokens(r);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [agentId]);

  const onMint = async (e) => {
    e.preventDefault();
    if (!form.merchant_vpa || !form.max_amount) {
      toast.error("Merchant VPA and max amount are required");
      return;
    }
    try {
      const r = await embedApi.mint(agentId, {
        ...form,
        expires_in_minutes: Number(form.expires_in_minutes) || 60,
      });
      setLast(r);
      toast.success("Embed token minted");
      setForm((f) => ({ ...f, max_amount: "", description: "" }));
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err.message || "mint failed");
    }
  };

  const onRevoke = async (token_id) => {
    try {
      await embedApi.revoke(agentId, token_id);
      toast.success("Token revoked");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err.message || "revoke failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-embed-tab">
      {/* Mint form */}
      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Mint embed token
        </div>
        <form onSubmit={onMint} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Field label="Merchant VPA">
            <input
              data-testid="embed-form-merchant-vpa"
              type="text"
              placeholder="globex@neopay"
              value={form.merchant_vpa}
              onChange={(e) => setForm({ ...form, merchant_vpa: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-mono text-xs focus:outline-none focus:border-foreground"
            />
          </Field>
          <Field label="Currency">
            <input
              data-testid="embed-form-currency"
              type="text"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-mono text-xs"
            />
          </Field>
          <Field label="Max amount">
            <input
              data-testid="embed-form-max-amount"
              type="text"
              placeholder="10.00"
              value={form.max_amount}
              onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-mono text-xs"
            />
          </Field>
          <Field label="Expires in (min)">
            <input
              data-testid="embed-form-expires"
              type="number"
              min={1}
              max={43200}
              value={form.expires_in_minutes}
              onChange={(e) => setForm({ ...form, expires_in_minutes: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-mono text-xs"
            />
          </Field>
          <Field label="Description (optional)" className="md:col-span-2">
            <input
              data-testid="embed-form-description"
              type="text"
              maxLength={280}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-xs"
            />
          </Field>
          <div className="md:col-span-2">
            <button
              type="submit"
              data-testid="embed-form-submit"
              className="px-3 py-1.5 border border-foreground bg-foreground text-background rounded-sm text-xs hover:opacity-90 inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" strokeWidth={1.5} /> Generate token
            </button>
          </div>
        </form>
      </div>

      {/* Last minted (one-time reveal) */}
      {last && (
        <div className="border border-status-ok/40 bg-status-ok/10 rounded-sm p-5" data-testid="embed-last-minted">
          <div className="text-[10px] uppercase tracking-[0.2em] text-status-ok mb-2">
            Token (shown ONCE — copy now)
          </div>
          <div className="font-mono text-[11px] break-all bg-background p-3 rounded-sm border border-border">
            {last.token}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2">
            <Snippet
              label="Public URL"
              value={`${FE_ORIGIN}/x402/${last.token}`}
              testid="embed-snippet-url"
            />
            <Snippet
              label="HTML anchor"
              value={`<a href="${FE_ORIGIN}/x402/${last.token}">Pay ${last.payload?.max_amount} ${last.payload?.currency} →</a>`}
              testid="embed-snippet-anchor"
            />
            <Snippet
              label="Iframe"
              value={`<iframe src="${FE_ORIGIN}/x402/${last.token}" width="420" height="520" style="border:1px solid #ccc;border-radius:4px"></iframe>`}
              testid="embed-snippet-iframe"
            />
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground">
            Closing or refreshing this view will lose the bare token. The
            list below shows revocable references but not the secret.
          </div>
        </div>
      )}

      {/* List */}
      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Active + historical tokens
        </div>
        {loading ? (
          <div className="p-5 text-xs text-muted-foreground">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground">No embed tokens yet.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="embed-token-list">
            {tokens.map((t) => (
              <div key={t.token_id} className="px-5 py-3 grid grid-cols-12 gap-3 items-center text-xs">
                <div className="col-span-4 min-w-0">
                  <div className="font-mono text-[11px] truncate">{t.token_id}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {t.payload?.merchant_vpa} · {t.payload?.max_amount} {t.payload?.currency}
                  </div>
                </div>
                <div className="col-span-2 font-mono text-[10px]">
                  <StatusPill s={t.status} />
                </div>
                <div className="col-span-2 font-mono text-[10px]">used {t.used_count}</div>
                <div className="col-span-3 font-mono text-[10px] text-muted-foreground">
                  exp {fmtTime(t.expires_at)}
                </div>
                <div className="col-span-1 text-right">
                  {t.status === "active" && (
                    <button
                      onClick={() => onRevoke(t.token_id)}
                      data-testid={`embed-revoke-${t.token_id}`}
                      className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:border-status-tampered hover:text-status-tampered transition-colors"
                      title="Revoke"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

function Snippet({ label, value, testid }) {
  return (
    <div className="border border-border rounded-sm bg-background overflow-hidden">
      <div className="px-3 py-1.5 border-b border-border flex items-center justify-between gap-2">
        <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success(`${label} copied`);
          }}
          data-testid={testid}
          className="text-[10px] inline-flex items-center gap-1 hover:underline"
        >
          <Copy className="w-3 h-3" strokeWidth={1.5} /> copy
        </button>
      </div>
      <div className="px-3 py-2 font-mono text-[10px] break-all">{value}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    revoked: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
    expired: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[9px] uppercase tracking-widest ${map[s] || "border-border"}`}>
      {s || "—"}
    </span>
  );
}
